// Live countdown: updates every [data-countdown-unit] child once per second until data-target.
// When the timer reaches zero while a visitor is watching, it fires a full-screen confetti burst.
// Add ?confetti to the page URL to preview the burst without waiting for the launch.
class Countdown extends HTMLElement {
  #timer = 0;
  #wasRunning = false;

  connectedCallback() {
    this.target = Date.parse(this.dataset.target || '');
    this.units = {
      days: this.querySelector('[data-countdown-unit="days"]'),
      hours: this.querySelector('[data-countdown-unit="hours"]'),
      minutes: this.querySelector('[data-countdown-unit="minutes"]'),
      seconds: this.querySelector('[data-countdown-unit="seconds"]'),
    };
    if (new URLSearchParams(window.location.search).has('confetti')) {
      window.setTimeout(() => this.#celebrate(), 600);
    }
    if (Number.isNaN(this.target)) return;
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #tick() {
    const remaining = Math.max(0, this.target - Date.now());
    const total = Math.floor(remaining / 1000);
    const values = {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
    };
    for (const [unit, el] of Object.entries(this.units)) {
      if (!el) continue;
      const text = String(values[unit]).padStart(2, '0');
      if (el.textContent !== text) el.textContent = text;
    }
    if (remaining > 0) {
      this.#wasRunning = true;
      return;
    }
    window.clearInterval(this.#timer);
    if (this.#wasRunning) {
      this.#wasRunning = false;
      this.dispatchEvent(new CustomEvent('countdown:complete', { bubbles: true }));
      this.#celebrate();
    }
  }

  // Full-viewport confetti in the theme palette. Never runs for visitors who prefer reduced motion.
  #celebrate() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const styles = getComputedStyle(document.documentElement);
    const palette = ['--color-accent', '--color-interactive', '--color-on-surface', '--color-border', '--color-surface-secondary']
      .map((name) => styles.getPropertyValue(name).trim())
      .filter(Boolean)
      .concat(['#C9A46A', '#E8C9A9']);

    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    Object.assign(canvas.style, {
      position: 'fixed',
      inset: '0',
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      zIndex: '2147483647',
    });
    document.body.append(canvas);

    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const width = () => window.innerWidth;
    const height = () => window.innerHeight;
    const rand = (min, max) => min + Math.random() * (max - min);
    const pieces = [];

    const piece = (x, y, angle, speed) => ({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      w: rand(6, 11),
      h: rand(8, 16),
      rot: rand(0, Math.PI * 2),
      spin: rand(-0.25, 0.25),
      tilt: rand(0, Math.PI * 2),
      tiltSpeed: rand(0.05, 0.15),
      color: palette[Math.floor(Math.random() * palette.length)],
      round: Math.random() < 0.25,
    });

    // Side cannons from both bottom corners.
    const cannons = (count) => {
      for (let i = 0; i < count; i++) {
        pieces.push(piece(0, height(), rand(-Math.PI * 0.42, -Math.PI * 0.18), rand(14, 24)));
        pieces.push(piece(width(), height(), rand(-Math.PI * 0.82, -Math.PI * 0.58), rand(14, 24)));
      }
    };
    // A shower across the full width from above.
    const shower = (count) => {
      for (let i = 0; i < count; i++) {
        pieces.push(piece(rand(0, width()), rand(-height() * 0.6, -20), rand(Math.PI * 0.4, Math.PI * 0.6), rand(1, 4)));
      }
    };

    cannons(110);
    shower(160);
    const waves = [
      window.setTimeout(() => cannons(80), 700),
      window.setTimeout(() => shower(140), 1400),
      window.setTimeout(() => cannons(60), 2200),
    ];

    const started = performance.now();
    const frame = (now) => {
      ctx.clearRect(0, 0, width(), height());
      for (let i = pieces.length - 1; i >= 0; i--) {
        const p = pieces[i];
        p.vx *= 0.985;
        p.vy = p.vy * 0.985 + 0.32;
        p.x += p.vx + Math.sin(p.tilt) * 0.6;
        p.y += p.vy;
        p.rot += p.spin;
        p.tilt += p.tiltSpeed;
        if (p.y > height() + 40) {
          pieces.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.scale(1, Math.cos(p.tilt));
        ctx.fillStyle = p.color;
        if (p.round) {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      }
      if (pieces.length || now - started < 2400) {
        window.requestAnimationFrame(frame);
      } else {
        waves.forEach((id) => window.clearTimeout(id));
        window.removeEventListener('resize', resize);
        canvas.remove();
      }
    };
    window.requestAnimationFrame(frame);
  }
}

customElements.define('s-countdown', Countdown);
